import { createBaseStore } from '@tasama/utils';
import type { BaseStoreState, StoreActions } from '@tasama/utils';
import type { SupportTicket } from '../types';

/**
 * Support Tickets Store using Zustand with @tasama/utils utilities
 * Manages state for open and closed support tickets
 */

interface SupportTicketsCustomState {
  // State
  openTickets: SupportTicket[];
  closedTickets: SupportTicket[];
  lastFetchedAt: {
    open: number | null;
    closed: number | null;
  };
  // Prefilled ticket data from ServiceCardL2
  prefilledTicketData: {
    subject: string | null;
    description: string | null;
  };
}

interface SupportTicketsCustomActions {
  // Actions
  setOpenTickets: (_tickets: SupportTicket[]) => void;
  setClosedTickets: (_tickets: SupportTicket[]) => void;
  addOpenTicket: (_ticket: SupportTicket) => void;
  updateTicket: (_ticketId: string, _updates: Partial<SupportTicket>) => void;
  markTicketAsRead: (_ticketId: string) => void;
  moveTicketToClosed: (_ticketId: string) => void;
  updateLastFetchedAt: (_type: 'open' | 'closed') => void;
  clearStore: () => void;
  // Prefilled ticket data actions
  setPrefilledTicketData: (
    _serviceTitle: string,
    _description?: string
  ) => void;
  getPrefilledTicketData: () => {
    subject: string | null;
    description: string | null;
  };
  clearPrefilledTicketData: () => void;
}

type SupportTicketsState = SupportTicketsCustomState &
  BaseStoreState &
  SupportTicketsCustomActions;

const initialState: Omit<
  SupportTicketsState,
  keyof BaseStoreState | keyof StoreActions
> = {
  openTickets: [],
  closedTickets: [],
  lastFetchedAt: {
    open: null,
    closed: null,
  },
  prefilledTicketData: {
    subject: null,
    description: null,
  },
  setOpenTickets: () => {},
  setClosedTickets: () => {},
  addOpenTicket: () => {},
  updateTicket: () => {},
  markTicketAsRead: () => {},
  moveTicketToClosed: () => {},
  updateLastFetchedAt: () => {},
  clearStore: () => {},
  setPrefilledTicketData: () => {},
  getPrefilledTicketData: () => ({ subject: null, description: null }),
  clearPrefilledTicketData: () => {},
};

export const useSupportTicketsStore = createBaseStore<SupportTicketsState>(
  initialState,
  (set, get) => ({
    // Actions
    setOpenTickets: (tickets: SupportTicket[]) => {
      set({ openTickets: tickets } as Partial<SupportTicketsState>);
    },

    setClosedTickets: (tickets: SupportTicket[]) => {
      set({ closedTickets: tickets } as Partial<SupportTicketsState>);
    },

    addOpenTicket: (ticket: SupportTicket) => {
      const { openTickets } = get();
      set({
        openTickets: [ticket, ...openTickets],
      } as Partial<SupportTicketsState>);
    },

    updateTicket: (ticketId: string, updates: Partial<SupportTicket>) => {
      const { openTickets, closedTickets } = get();

      // Update in open tickets
      const updatedOpenTickets = openTickets.map((ticket: SupportTicket) =>
        ticket.ticket_id === ticketId ? { ...ticket, ...updates } : ticket
      );

      // Update in closed tickets
      const updatedClosedTickets = closedTickets.map((ticket: SupportTicket) =>
        ticket.ticket_id === ticketId ? { ...ticket, ...updates } : ticket
      );

      set({
        openTickets: updatedOpenTickets,
        closedTickets: updatedClosedTickets,
      } as Partial<SupportTicketsState>);
    },

    markTicketAsRead: (ticketId: string) => {
      const { updateTicket } = get();
      updateTicket(ticketId, { isUnread: false });
    },

    moveTicketToClosed: (ticketId: string) => {
      const { openTickets, closedTickets } = get();
      const ticket = openTickets.find(
        (t: SupportTicket) => t.ticket_id === ticketId
      );

      if (ticket) {
        const updatedTicket = {
          ...ticket,
          status: 'closed' as const,
          closureDate: new Date().toISOString(),
        };

        set({
          openTickets: openTickets.filter(
            (t: SupportTicket) => t.ticket_id !== ticketId
          ),
          closedTickets: [updatedTicket, ...closedTickets],
        } as Partial<SupportTicketsState>);
      }
    },

    updateLastFetchedAt: (type: 'open' | 'closed') => {
      const { lastFetchedAt } = get();
      set({
        lastFetchedAt: {
          ...lastFetchedAt,
          [type]: Date.now(),
        },
      } as Partial<SupportTicketsState>);
    },

    clearStore: () => {
      set({
        ...initialState,
        loading: false,
        error: null,
      } as Partial<SupportTicketsState>);
    },

    // Prefilled ticket data actions
    setPrefilledTicketData: (subject: string, description: string) => {
      set({
        prefilledTicketData: {
          subject,
          description,
        },
      } as Partial<SupportTicketsState>);
    },

    getPrefilledTicketData: () => {
      const { prefilledTicketData } = get();
      return prefilledTicketData;
    },

    clearPrefilledTicketData: () => {
      set({
        prefilledTicketData: {
          subject: null,
          description: null,
        },
      } as Partial<SupportTicketsState>);
    },
  })
);

// Export store selectors for easy access
export const useOpenTickets = () =>
  useSupportTicketsStore((state) => state.openTickets);
export const useClosedTickets = () =>
  useSupportTicketsStore((state) => state.closedTickets);
export const useTicketsLoading = () =>
  useSupportTicketsStore((state) => state.loading);
export const useTicketsError = () =>
  useSupportTicketsStore((state) => state.error);
export const useTicketsLastFetchedAt = () =>
  useSupportTicketsStore((state) => state.lastFetchedAt);
export const usePrefilledTicketData = () =>
  useSupportTicketsStore((state) => state.prefilledTicketData);
